/**
 * BotSpot Web Dashboard - useMetrics Hook
 *
 * React hook for fetching and auto-updating bot metrics.
 */

import { useEffect, useState } from "react";
import { getBotMetrics } from "../api/bots";
import { getErrorMessage } from "../api/client";
import type { BotMetrics } from "../api/types";

/**
 * Hook options
 */
interface UseMetricsOptions {
  /**
   * Polling interval in milliseconds (default: 5000ms = 5s)
   */
  pollInterval?: number;
  /**
   * Whether to enable automatic polling (default: true)
   */
  enablePolling?: boolean;
}

/**
 * Hook return value
 */
interface UseMetricsReturn {
  metrics: BotMetrics | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * useMetrics Hook
 *
 * Fetches bot metrics and optionally polls for updates.
 *
 * @param botId - Bot ID to fetch metrics for
 * @param options - Hook configuration options
 * @returns Metrics data, loading state, error, and refresh function
 *
 * @example
 * ```tsx
 * const { metrics, loading, error, refresh } = useMetrics(botId);
 *
 * if (loading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error}</div>;
 * if (!metrics) return null;
 *
 * return <div>CPU Usage: {metrics.cpu.usage_percent}%</div>;
 * ```
 */
export function useMetrics(
  botId: string | undefined,
  options: UseMetricsOptions = {}
): UseMetricsReturn {
  const { pollInterval = 5000, enablePolling = true } = options;

  const [metrics, setMetrics] = useState<BotMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch metrics from API
   */
  const fetchMetrics = async () => {
    if (!botId) {
      setError("Bot ID is required");
      setLoading(false);
      return;
    }

    try {
      const data = await getBotMetrics(botId);
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh metrics (callable from component)
   */
  const refresh = async () => {
    setLoading(true);
    await fetchMetrics();
  };

  /**
   * Initial fetch and polling setup
   */
  useEffect(() => {
    if (!botId) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchMetrics();

    // Set up polling if enabled
    if (enablePolling) {
      const interval = setInterval(() => {
        if (botId) {
          getBotMetrics(botId)
            .then((data) => {
              setMetrics(data);
              setError(null);
            })
            .catch((err) => {
              // Silent fail on polling errors to avoid UI disruption
              console.error("Metrics polling error:", err);
            });
        }
      }, pollInterval);

      return () => clearInterval(interval);
    }
  }, [botId, pollInterval, enablePolling]);

  return {
    metrics,
    loading,
    error,
    refresh,
  };
}
